import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Alert from '../components/Alert';
import Confirm from '../components/Confirm';
import Toast from '../components/Toast';
import DataTable from '../components/DataTable';
import AutoComplete from '../components/AutoComplete';
import { almacenService, sucursalService, ubigeoService } from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import '../styles/datatable.css';

const MODOS_PARTE = [
  { value: 'manual', label: 'Manual' },
  { value: 'autoincremental', label: 'Autoincremental (correlativo)' },
  { value: 'anio_mes_numero', label: 'Año-Mes-Numero (260300000001)' },
];

const getModoLabel = (val) => {
  const m = MODOS_PARTE.find(x => x.value === val);
  return m ? m.label : val || '--';
};

const Almacenes = () => {
  const [records, setRecords] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [ubigeos, setUbigeos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [alert, setAlert] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);

  const emptyForm = {
    codigo: '', nombre: '', id_sucursal: '', direccion: '', ubigeo: '',
    codigo_sunat: '', considerar_costeo: true,
    modo_parte_entrada: 'manual', modo_parte_salida: 'manual',
    siguiente_parte_entrada: 1, siguiente_parte_salida: 1,
    ultimo_numero_pe: 0, ultimo_numero_ps: 0,
  };
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [alm, suc, ubi] = await Promise.all([
        almacenService.getAll(),
        sucursalService.getAll(),
        ubigeoService.getAll(),
      ]);
      setRecords(alm.data);
      setSucursales(suc.data);
      setUbigeos(ubi.data);
    } catch {
      setAlert({ type: 'error', message: 'Error al cargar almacenes.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setForm(emptyForm); setEditing(null); setShowModal(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      codigo: r.codigo || '',
      nombre: r.nombre || '',
      id_sucursal: r.id_sucursal || '',
      direccion: r.direccion || '',
      ubigeo: r.ubigeo || '',
      codigo_sunat: r.codigo_sunat || '',
      considerar_costeo: r.considerar_costeo === true || r.considerar_costeo === 1,
      modo_parte_entrada: r.modo_parte_entrada || 'manual',
      modo_parte_salida: r.modo_parte_salida || 'manual',
      siguiente_parte_entrada: r.siguiente_parte_entrada ?? 1,
      siguiente_parte_salida: r.siguiente_parte_salida ?? 1,
      ultimo_numero_pe: r.ultimo_numero_pe ?? 0,
      ultimo_numero_ps: r.ultimo_numero_ps ?? 0,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.codigo || !form.nombre) { setAlert({ type: 'warning', message: 'Codigo y nombre son obligatorios.' }); return; }
    const data = {
      codigo: form.codigo,
      nombre: form.nombre,
      id_sucursal: form.id_sucursal === '' ? null : parseInt(form.id_sucursal),
      direccion: form.direccion || null,
      ubigeo: form.ubigeo || null,
      codigo_sunat: form.codigo_sunat || null,
      considerar_costeo: form.considerar_costeo,
      modo_parte_entrada: form.modo_parte_entrada,
      modo_parte_salida: form.modo_parte_salida,
      siguiente_parte_entrada: parseInt(form.siguiente_parte_entrada) || 1,
      siguiente_parte_salida: parseInt(form.siguiente_parte_salida) || 1,
      ultimo_numero_pe: parseInt(form.ultimo_numero_pe) || 0,
      ultimo_numero_ps: parseInt(form.ultimo_numero_ps) || 0,
    };
    try {
      if (editing) await almacenService.update(editing.id_almacen, data);
      else await almacenService.create(data);
      setShowModal(false); fetchData();
    } catch (err) {
      setAlert({ type: 'error', message: getErrorDetail(err, 'Error al guardar.') });
    }
  };

  const handleDelete = (r) => {
    setConfirm({
      title: 'Eliminar Almacen', message: `¿Eliminar "${r.nombre}"?`,
      onConfirm: async () => {
        setConfirm(null);
        try { await almacenService.delete(r.id_almacen); setToast('Almacen eliminado'); fetchData(); }
        catch { setAlert({ type: 'error', message: 'Error al eliminar.' }); }
      },
    });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const columns = [
    { key: 'codigo', label: 'Codigo', width: 90, style: { fontWeight: 600 } },
    { key: 'nombre', label: 'Nombre' },
    { key: 'sucursal_nombre', label: 'Sucursal', width: 150, render: (r) => r.sucursal_nombre || '--' },
    { key: 'codigo_sunat', label: 'Cod. SUNAT', width: 95 },
    { key: 'considerar_costeo', label: 'Costeo', width: 70, style: { textAlign: 'center' }, render: (r) => (r.considerar_costeo === true || r.considerar_costeo === 1) ? 'Si' : 'No' },
    { key: 'modo_parte_entrada', label: 'Modo PE', width: 120, render: (r) => getModoLabel(r.modo_parte_entrada) },
    { key: 'ultimo_numero_pe', label: 'Últ. PE', width: 75, style: { textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }, render: (r) => r.ultimo_numero_pe ?? 0 },
    { key: 'modo_parte_salida', label: 'Modo PS', width: 120, render: (r) => getModoLabel(r.modo_parte_salida) },
    { key: 'ultimo_numero_ps', label: 'Últ. PS', width: 75, style: { textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }, render: (r) => r.ultimo_numero_ps ?? 0 },
  ];

  return (
    <Layout title="Almacenes" subtitle="Utilitarios">
      <div className="page-header">
        <h2>Almacenes</h2>
        <button className="btn btn-primary" onClick={openCreate}>+ Nuevo</button>
      </div>

      <DataTable columns={columns} data={records} idField="id_almacen" onEdit={openEdit} onDelete={handleDelete}
        loading={loading} emptyMessage="No hay almacenes registrados" exportFileName="almacenes" />

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:620}} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
                <h2>{editing ? 'Editar Almacen' : 'Almacen'}</h2>
                {!editing && <span className="modal-header-badge"><svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd"/></svg></span>}
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div style={{display:'grid',gridTemplateColumns:'140px 1fr',gap:'0 16px'}}>
                  <div className="form-row"><label>Codigo *</label><input name="codigo" value={form.codigo} onChange={handleChange} placeholder="Ej: ALM01" autoFocus /></div>
                  <div className="form-row"><label>Nombre *</label><input name="nombre" value={form.nombre} onChange={handleChange} placeholder="Nombre del almacen" /></div>
                </div>

                <div className="form-row">
                  <label>Sucursal</label>
                  <select name="id_sucursal" value={form.id_sucursal} onChange={handleChange}>
                    <option value="">-- Sin sucursal --</option>
                    {sucursales.map(s => <option key={s.id_sucursal} value={s.id_sucursal}>{s.codigo} - {s.nombre}</option>)}
                  </select>
                </div>

                <div className="form-row"><label>Direccion</label><input name="direccion" value={form.direccion} onChange={handleChange} placeholder="Direccion del almacen" /></div>

                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'0 16px'}}>
                  <AutoComplete
                    label="Ubigeo"
                    value={form.ubigeo}
                    options={ubigeos}
                    idField="codigo"
                    displayField="descripcion"
                    onChange={(val) => setForm(f => ({ ...f, ubigeo: val }))}
                    placeholder="Buscar ubigeo..."
                  />
                  <div className="form-row"><label>Codigo SUNAT</label><input name="codigo_sunat" value={form.codigo_sunat} onChange={handleChange} placeholder="Cod. SUNAT" /></div>
                  <div className="form-row" style={{display:'flex',alignItems:'center',gap:'8px',paddingTop:'22px'}}>
                    <input type="checkbox" name="considerar_costeo" checked={form.considerar_costeo} onChange={handleChange} id="chk_costeo" style={{width:'auto',margin:0}} />
                    <label htmlFor="chk_costeo" style={{marginBottom:0,cursor:'pointer',fontSize:'13px'}}>Considerar para costeo (Kardex)</label>
                  </div>
                </div>

                {/* Numeración PE */}
                <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: 'var(--accent)', marginBottom: 8 }}>
                    Numeración de Partes
                  </div>

                  {/* Partes de Entrada */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px', gap: '0 16px', alignItems: 'end' }}>
                    <div className="form-row" style={{ marginBottom: 0 }}>
                      <label>Partes de Entrada — modo numeración</label>
                      <select name="modo_parte_entrada" value={form.modo_parte_entrada} onChange={handleChange}>
                        {MODOS_PARTE.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                    </div>
                    <div className="form-row" style={{ marginBottom: 0 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        Último correlativo
                        <span style={{ fontWeight: 400, fontSize: 10, color: 'var(--text-muted)', background: 'var(--bg-hover)', borderRadius: 4, padding: '1px 6px', letterSpacing: 0.5 }}>PE</span>
                      </label>
                      <input
                        type="number"
                        name="ultimo_numero_pe"
                        value={form.ultimo_numero_pe}
                        onChange={handleChange}
                        min={0}
                        style={{ fontFamily: 'monospace', fontWeight: 700 }}
                      />
                    </div>
                  </div>

                  {/* Partes de Salida */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px', gap: '0 16px', alignItems: 'end', marginTop: 6 }}>
                    <div className="form-row" style={{ marginBottom: 0 }}>
                      <label>Partes de Salida — modo numeración</label>
                      <select name="modo_parte_salida" value={form.modo_parte_salida} onChange={handleChange}>
                        {MODOS_PARTE.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                    </div>
                    <div className="form-row" style={{ marginBottom: 0 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        Último correlativo
                        <span style={{ fontWeight: 400, fontSize: 10, color: 'var(--text-muted)', background: 'var(--bg-hover)', borderRadius: 4, padding: '1px 6px', letterSpacing: 0.5 }}>PS</span>
                      </label>
                      <input
                        type="number"
                        name="ultimo_numero_ps"
                        value={form.ultimo_numero_ps}
                        onChange={handleChange}
                        min={0}
                        style={{ fontFamily: 'monospace', fontWeight: 700 }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Guardar' : 'Crear'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {alert && <Alert type={alert.type} title={alert.title} message={alert.message} onClose={() => setAlert(null)} />}
      {confirm && <Confirm title={confirm.title} message={confirm.message} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </Layout>
  );
};

export default Almacenes;
